import { NgIf } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { GalleryDto } from '@picflow/types';
import { Subscription } from 'rxjs';
import { sleep } from 'src/app/lib/sleep';
import { AssetService } from 'src/app/services/asset.service';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { GalleryPasswordService } from 'src/app/services/gallery-password.service';
import { GalleryService } from 'src/app/services/gallery.service';
import { SectionService } from 'src/app/services/section.service';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { InferredHoverDirective } from '../../../shared/directives/inferred-hover.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { GalleryTeaserUrlPipe } from '../../../shared/pipes/gallery-teaser-url.pipe';
import { RelativeTimePipe } from '../../pipes/relative-time.pipe';

@Component({
  selector: 'app-gallery-item',
  templateUrl: './gallery-item.component.html',
  styleUrls: ['./gallery-item.component.scss'],
  standalone: true,
  imports: [InferredHoverDirective, NgIf, SvgComponent, TooltipDirective, RelativeTimePipe, GalleryTeaserUrlPipe],
})
export class GalleryItemComponent implements OnChanges, OnDestroy {
  @Input() gallery: GalleryDto;
  @Input() compact: boolean;
  @Input() publicMode: boolean;
  @Input() darkBackground: boolean;
  @Input() showPasswordProtected: boolean;
  @Output() onInView = new EventEmitter();
  @ViewChild('galleryItem') galleryItem: ElementRef<HTMLDivElement>;
  assetCount: number;
  isInView = false;
  buffer = 20;
  assetUpdateSub: Subscription;
  galleryUpdateSub: Subscription;
  sectionUpdateSub: Subscription;

  @HostListener('window:scroll', ['$event'])
  isScrolledIntoView() {
    this.inViewCheck();
  }

  constructor(
    public featureFlags: FeatureFlagsService,
    private galleryService: GalleryService,
    private galleryPasswordService: GalleryPasswordService,
    private assetService: AssetService,
    private sectionService: SectionService
  ) {
    this.assetUpdateSub = this.assetService.onUpdates.subscribe(event => {
      if (!event) return;
      if (event[1] !== 'asset_moved') return;
      if (!this.gallery) return;
      if (!this.isInView) return;

      if (event[2] !== this.gallery.id) {
        this.assetCount -= 1;
      }
      if (event[3] !== this.gallery.id) {
        this.assetCount += 1;
      }
    });
    this.galleryUpdateSub = this.galleryService.onUpdates.subscribe(event => {
      if (!event) return;
      if (event[0] === 'deleted') this.inViewCheck();
      if (event[0] === 'updated' && event[1] === this.gallery?.id) {
        this.inViewCheck();
      }
    });
    this.sectionUpdateSub = this.sectionService.onUpdates.subscribe(event => {
      if (!event) return;
      if (event[1] === 'updated') this.inViewCheck();
    });
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (!changes.gallery) return;
    this.assetCount = this.gallery.total_assets_count;
    await sleep(0);
    this.inViewCheck();
    await sleep(10);
    this.inViewCheck();
  }

  private async getMainGalleryAssetCount() {
    if (this.gallery.section) return;
    if (this.gallery.total_assets_count !== undefined) {
      this.assetCount = this.gallery.total_assets_count;
      return;
    }
    if (!this.isInView) return;
    if (!this.canGetAssetCount()) return;
    const subGalleries = await this.galleryService.getSubGalleries(this.gallery.id);
    this.assetCount = [this.gallery, ...subGalleries].reduce((t, gallery) => t + gallery.assets_count, 0);
  }

  private inViewCheck() {
    if (!this.galleryItem) return;
    if (this.isInView) return;

    const rect = this.galleryItem.nativeElement.getBoundingClientRect();
    this.isInView = rect.top < window.innerHeight + this.buffer;
    if (this.isInView) {
      this.getMainGalleryAssetCount();
      this.onInView.emit();
    }
  }

  private canGetAssetCount() {
    if (!this.publicMode) return true;
    if (!this.gallery.password_enabled) return true;
    if (this.galleryPasswordService.getPassword(this.gallery.id)) return true;
    return false;
  }

  ngOnDestroy(): void {
    this.assetUpdateSub.unsubscribe();
    this.galleryUpdateSub.unsubscribe();
    this.sectionUpdateSub.unsubscribe();
  }
}
